import { getColorByBgColor } from "@/lib/colors";
import { getMember, getOrganization, getPortrait } from "@/store/auth/authSlice";
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LogoutIcon from '@mui/icons-material/Logout';
import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import RestoreIcon from '@mui/icons-material/Restore';
import SettingsIcon from '@mui/icons-material/Settings';
import SwitchAccessShortcutIcon from '@mui/icons-material/SwitchAccessShortcut';
import { MenuList } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Link } from "@tanstack/react-router";
import { useFlag } from "@unleash/proxy-client-react";
import { t } from "i18next";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { useAuth } from "react-oidc-context";
import { useSelector } from "react-redux";
import useResponsive from "./hooks/useResponsive";

export default function UserAvatar() {

    const auth = useAuth()

    const sessionMember = useSelector(getMember);
    const sessionOrganization = useSelector(getOrganization);
    const memberPortrait = useSelector(getPortrait);

    const { minMedium } = useResponsive();

    const permissionConfig = useFlag('permission_config');

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const openMenu = (event: React.MouseEvent<HTMLElement>) => {
        if (auth.isAuthenticated) {
            setAnchorEl(event.currentTarget);
        }
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const [resetDialogOpen, setResetDialogOpen] = React.useState(false);

    const handleCloseDialog = () => {
        setResetDialogOpen(false);
    };

    const resetSettings = () => {
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('scoreg')) {
                localStorage.removeItem(key)
            }
        })
        setResetDialogOpen(false);
        enqueueSnackbar(t('message.resetSettings'), { variant: 'success' });
    }

    const readable = sessionMember.permissions ? sessionMember.permissions.some((perm) => perm === 'read') : false

    if (!sessionMember || !sessionMember.uuid) {
        return <></>
    }

    return (<>
        <Tooltip title={sessionMember && sessionMember.uuid ? (sessionMember.firstname + " " + sessionMember.lastname) : ''}>
            <>
                <Typography style={{ whiteSpace: "nowrap", display: "inline" }} onClick={openMenu} sx={{ cursor: 'pointer', color: getColorByBgColor(sessionOrganization?.color) }}>
                    {minMedium &&
                        <>
                            <span>{sessionMember && sessionMember.uuid ? ("| " + sessionMember.firstname + " " + sessionMember.lastname + " | " + sessionMember.scoutId + " ") : ''}</span>
                            <span>&nbsp;&nbsp;</span>
                        </>
                    }
                    <IconButton sx={{ p: 0 }} >
                        <Avatar src={`data:image/jpeg;base64,${memberPortrait && memberPortrait.base64}`} >
                            {sessionMember && sessionMember.uuid ? (sessionMember.firstname?.charAt(0) + " " + sessionMember.lastname?.charAt(0)) : "?"}
                        </Avatar>
                    </IconButton>
                </Typography>
            </>
        </Tooltip>
        <Menu
            anchorEl={anchorEl}
            id="userAvatarMenu"
            open={open}
            onClose={handleClose}
            onClick={handleClose}>
            <MenuList dense>
                {readable &&
                    <Link
                        to="/member/$memberUuid/main"
                        params={{ memberUuid: sessionMember?.uuid }}
                        style={{ color: 'white' }}
                    >
                        <MenuItem>
                            <ListItemIcon>
                                <PermIdentityIcon />
                            </ListItemIcon>
                            <ListItemText secondary={t('menu.userAvatar.masterdata')} />
                        </MenuItem>
                    </Link>
                }
                <MenuItem onClick={() => setResetDialogOpen(true)}>
                    <ListItemIcon>
                        <RestoreIcon />
                    </ListItemIcon>
                    <ListItemText secondary={t('menu.userAvatar.resetSettings')} />
                </MenuItem>
                {
                    permissionConfig &&
                    <Link
                        to={'/permissionConfig'}
                        activeProps={{
                            className: 'scoreg-active-button',
                        }}
                        activeOptions={{
                            includeSearch: false
                        }}
                    >
                        <MenuItem>
                            <ListItemIcon>
                                <LockOpenIcon />
                            </ListItemIcon>
                            <ListItemText secondary={t('menu.permissionConfig')} />
                        </MenuItem>
                    </Link>
                }
                <Link
                    to={'/settings'}
                    activeProps={{
                        className: 'scoreg-active-button',
                    }}
                    activeOptions={{
                        includeSearch: false
                    }}
                >
                    <MenuItem>
                        <ListItemIcon>
                            <SettingsIcon />
                        </ListItemIcon>
                        <ListItemText secondary={t('menu.settings')} />
                    </MenuItem>
                </Link>
                <Link
                    to={'/help'}
                    activeProps={{
                        className: 'scoreg-active-button',
                    }}
                    activeOptions={{
                        includeSearch: false
                    }}
                >
                    <MenuItem>
                        <ListItemIcon>
                            <HelpOutlineIcon />
                        </ListItemIcon>
                        <ListItemText secondary={t('menu.help')} />
                    </MenuItem>
                </Link>
                <Divider />
                <Link
                    to={'/switchOrganization'}
                >
                    <MenuItem>
                        <ListItemIcon>
                            <SwitchAccessShortcutIcon />
                        </ListItemIcon>
                        <ListItemText secondary={t('menu.switchOrganization')} />
                    </MenuItem>
                </Link>
                <MenuItem onClick={() => {
                    // auth.removeUser()
                    auth.signoutRedirect({ id_token_hint: auth.user?.id_token });
                }
                }>
                    <ListItemIcon>
                        <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText secondary={t('action.logout')} />
                </MenuItem>
            </MenuList>
        </Menu>
        <Dialog
            open={resetDialogOpen}
            onClose={handleCloseDialog}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">
                {t('title.resetSettings')}
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    {t('message.resetSettingsDescription')}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseDialog}>{t('action.cancel')}</Button>
                <Button onClick={resetSettings} autoFocus>
                    {t('action.ok')}
                </Button>
            </DialogActions>
        </Dialog>
    </>)
}